import type { FC, PropsWithChildren } from 'react';
import { createContext, useMemo } from 'react';
import { useLocalStorage } from 'react-use';

import useContextOrThrowError from '@zen/utils/hooks/useContextOrThrowError';
import type { Undefinable } from '@zen/utils/typescript';

import { PurchaseOrderTableVersion } from './types';

export const PURCHASE_ORDER_TABLE_VERSION_STORAGE_KEY: string = 'purchase-order-table-version-preference';
const DEFAULT_PURCHASE_ORDER_TABLE_VERSION: PurchaseOrderTableVersion = PurchaseOrderTableVersion.NEW;

interface PurchaseOrderTableVersionContextValue {
  setTableVersion: (tableVersion: PurchaseOrderTableVersion) => void;
  tableVersion: PurchaseOrderTableVersion;
}

const PurchaseOrderTableVersionContext = createContext<Undefinable<PurchaseOrderTableVersionContextValue>>(undefined);

export const PurchaseOrderTableVersionProvider: FC<PropsWithChildren> = ({ children }) => {
  const [storedTableVersion, setStoredTableVersion] = useLocalStorage<PurchaseOrderTableVersion>(
    PURCHASE_ORDER_TABLE_VERSION_STORAGE_KEY
  );

  const value: PurchaseOrderTableVersionContextValue = useMemo(
    () => ({
      setTableVersion: (newTableVersion: PurchaseOrderTableVersion): void => {
        setStoredTableVersion(newTableVersion);
      },
      tableVersion: storedTableVersion ?? DEFAULT_PURCHASE_ORDER_TABLE_VERSION
    }),
    [setStoredTableVersion, storedTableVersion]
  );

  return <PurchaseOrderTableVersionContext.Provider value={value}>{children}</PurchaseOrderTableVersionContext.Provider>;
};

export const usePurchaseOrderTableVersion = (): PurchaseOrderTableVersionContextValue => {
  return useContextOrThrowError<PurchaseOrderTableVersionContextValue>(
    PurchaseOrderTableVersionContext,
    'usePurchaseOrderTableVersion must be used within PurchaseOrderTableVersionProvider'
  );
};

export type { PurchaseOrderTableVersionContextValue };
export default PurchaseOrderTableVersionContext;
